import { Injectable } from '@angular/core';
import * as CryptoJS from 'crypto-js';
import { environment } from '../../environment';
import { map, tap, catchError, switchMap } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class DealsOffersService {
  private readonly encryptionKey = '6d66fb7debfd15bf716bb14752b9603b';
  private readonly encryptionIV = '716bb14752b9603b';
  private readonly baseUrl = environment.voucherApi.baseUrl;
  private token: string | null = null; // In-memory token storage
  private brands: Brand[] | null = null; // In-memory brands storage

  constructor(private http: HttpClient) { }

  private decryptData(encryptedData: string): string {
    try {
      const key = CryptoJS.enc.Utf8.parse(this.encryptionKey);
      const iv = CryptoJS.enc.Utf8.parse(this.encryptionIV);

      const decrypted = CryptoJS.AES.decrypt(encryptedData, key, {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      });

      return decrypted.toString(CryptoJS.enc.Utf8);
    } catch (e) {
      //console.error('Decryption failed:', e);
      throw e;
    }
  }

  private encryptData(data: string): string {
    try {
      const key = CryptoJS.enc.Utf8.parse(this.encryptionKey);
      const iv = CryptoJS.enc.Utf8.parse(this.encryptionIV);

      const encrypted = CryptoJS.AES.encrypt(data, key, {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      });

      return encrypted.toString();
    } catch (e) {
      // console.error('Encryption failed:', e);
      throw e;
    }
  }

  /**
   * Get Token for Deals API
   */
  getToken(forceRefresh: boolean = false): Observable<TokenResponse> {

    if (!forceRefresh && this.token) {
      // Return cached token from memory as observable
      return of({
        status: 'success',
        data: '',
        token: this.token,
        desc: 'Loaded from memory'
      });
    }

    // Using the proxy path /bulkgv mapped to https://send.bulkgv.net
    const url = `${this.baseUrl}/API/v1/gettoken`;
    const headers = new HttpHeaders({
      'username': environment.voucherApi.username,
      'password': environment.voucherApi.password,
      'Content-Type': 'application/json'
    });
    return this.http.get<any>(url, { headers }).pipe(
      map(response => {
        if (response && response.status === 'success' && response.data) {
          let decryptedToken = this.decryptData(response.data);
          // Remove potential double quotes from the decrypted string
          decryptedToken = decryptedToken.replace(/^"|"$/g, '').trim();

          // Store in memory
          this.token = decryptedToken;

          return { ...response, token: decryptedToken };
        }
        return response;
      }),
      catchError(err => {
        // If token fetch fails, clear possibly stale memory
        this.token = null;
        throw err;
      })
    );
  }

  /**
   * Get Brands for Deals API
   */
  /**
   * Get Brands for Deals API
   */
  getBrands(token: string, forceRefresh: boolean = false): Observable<Brand[]> {

    if (!forceRefresh && this.brands && this.brands.length > 0) {
      return of(this.brands);
    }

    // 2. Fetch from API
    const url = `${this.baseUrl}/API/v1/getbrands`;
    const headers = new HttpHeaders({
      'token': token,
      'Content-Type': 'application/json'
    });
    const body = {
      "payload": "28JNaliL3k+bcBiM//mvnMQNmBSdPwOAQjJ8ZeDQ8KY="
    };

    return this.http.post<DealsResponse>(url, body, { headers }).pipe(
      map(response => {
        if (response && response.status === 'success' && response.data) {
          const decryptedData = this.decryptData(response.data);
          try {
            const brands = JSON.parse(decryptedData) as Brand[];

            // 3. Store valid data in memory
            if (brands && brands.length > 0) {
              this.brands = brands;
            }

            return brands;
          } catch (e) {
            // console.error('Error parsing brands data:', e);
            return [];
          }
        }
        // Check for invalid token
        if (response && response.code === 'ER083') {
          throw { code: 'ER083', message: response.desc };
        }

        // If status is not success, treat as failure (could be token expiry)
        if (response && response.status !== 'success') {
          throw new Error(response.desc || 'Failed to fetch brands');
        }
        return [];
      })
    );
  }

  /**
   * Orchestrates fetching brands with automatic token management
   */
  getDealsWithAuth(): Observable<Brand[]> {
    return this.getToken(false).pipe(
      switchMap((tokenResp: TokenResponse) => {
        if (tokenResp && tokenResp.token) {
          // Try fetching brands (will use cache if available and not forced)
          return this.getBrands(tokenResp.token, false).pipe(
            catchError(error => {
              // If fetching brands fails (invlaid token),
              // try refreshing the token and fetching again
              // Check if specific error code ER083 or general error (existing logic covers general too, but let's be cleaner)
              if (error.code === 'ER083' || error.message === 'Invalid token' || true) { // strict 'true' keeps existing permissive retry behavior which is safe
                // console.warn('First attempt to fetch brands failed, refreshing token...', error);
                return this.getToken(true).pipe(
                  switchMap((newTokenResp: TokenResponse) => {
                    if (newTokenResp && newTokenResp.token) {
                      // Force refresh brands on retry to ensure valid data with new token
                      return this.getBrands(newTokenResp.token, true);
                    }
                    throw new Error('Failed to refresh token');
                  })
                );
              }
              throw error;
            })
          );
        }
        throw new Error('No token received');
      })
    );
  }

  /**
   * Pull Voucher API
   */
  pullVoucher(brandProductCode: string, denomination: string, quantity: number, externalOrderId: string): Observable<any> {
    return this.getToken(false).pipe(
      switchMap((tokenResp: TokenResponse) => {
        if (!tokenResp || !tokenResp.token) {
          throw new Error('No token available');
        }

        const makeRequest = (token: string) => {
          const url = `${this.baseUrl}/API/v1/pullvoucher`;
          const headers = new HttpHeaders({
            'token': token,
            'Content-Type': 'application/json'
          });

          const payloadObj = {
            "BrandProductCode": brandProductCode,
            "Denomination": denomination,
            "Quantity": quantity,
            "ExternalOrderId": externalOrderId
          };

          const encryptedPayload = this.encryptData(JSON.stringify(payloadObj));
          const body = {
            "payload": encryptedPayload
          };

          return this.http.post<DealsResponse>(url, body, { headers });
        };

        return makeRequest(tokenResp.token).pipe(
          switchMap(response => {
            // Check for Invalid Token ER083
            if (response && response.code === 'ER083') {
              // Force refresh token
              return this.getToken(true).pipe(
                switchMap(newTokenResp => {
                  if (newTokenResp && newTokenResp.token) {
                    return makeRequest(newTokenResp.token);
                  }
                  return of(response); // Return original error if token refresh fails
                })
              );
            }
            return of(response);
          }),
          map(response => {
            if (response && response.status === 'success' && response.data) {
              try {
                const decryptedData = this.decryptData(response.data);
                return JSON.parse(decryptedData);
              } catch (e) {
                // console.error('Error decrypting pull voucher response:', e);
                return null;
              }
            }
            return response;
          })
        );
      })
    );
  }
  /**
   * Check Stock Availability
   */
  checkStock(brandProductCode: string, denomination: string): Observable<any> {
    return this.getToken(false).pipe(
      switchMap((tokenResp: TokenResponse) => {
        if (!tokenResp || !tokenResp.token) {
          throw new Error('No token available');
        }

        const makeRequest = (token: string) => {
          const url = `${this.baseUrl}/API/v1/getstock`;
          const headers = new HttpHeaders({
            'token': token,
            'Content-Type': 'application/json'
          });

          const payloadObj = {
            "BrandProductCode": brandProductCode,
            "Denomination": denomination
          };

          const encryptedPayload = this.encryptData(JSON.stringify(payloadObj));
          const body = {
            "payload": encryptedPayload
          };
          return this.http.post<DealsResponse>(url, body, { headers });
        };

        return makeRequest(tokenResp.token).pipe(
          switchMap(response => {
            // Check for Invalid Token ER083
            if (response && response.code === 'ER083') {
              // Force refresh token
              return this.getToken(true).pipe(
                switchMap(newTokenResp => {
                  if (newTokenResp && newTokenResp.token) {
                    return makeRequest(newTokenResp.token);
                  }
                  return of(response); // Return original error if token refresh fails
                })
              );
            }
            return of(response);
          }),
          map(response => {
            if (response && response.status === 'success' && response.data) {
              try {
                const decryptedData = this.decryptData(response.data);
                return JSON.parse(decryptedData);
              } catch (e) {
                // console.error('Error decrypting stock response:', e);
                return null;
              }
            }
            return response;
          })
        );
      })
    );
  }
}
export interface TokenResponse {
  status?: string;
  data?: string;
  desc?: string;
  code?: string;
  token?: string;
  [key: string]: any;
}
export interface DealsResponse {
  status?: string;
  data?: string; // Encrypted data
  desc?: string;
  code?: string;
}

export interface Brand {
  BrandProductCode: string;
  BrandName: string;
  Brandtype: string;
  RedemptionType: string;
  OnlineRedemptionUrl: string;
  BrandImage: string;
  denominationList: string;
  MinValue: string | null;
  MaxValue: string | null;
  DenomType: string;
  stockAvailable: string;
  Category: string;
  Descriptions?: string;
  tnc?: string;
  importantInstruction?: string;
  redeemSteps?: string;
  updated_at?: string;
  [key: string]: any;
}